import { makeAutoObservable, observable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields } from "@/lib/changed-fields";
import type { RowSelectionState, SortingState } from "@tanstack/react-table";

import { pollExportStatus } from "@/components/data-table/use-csv-export";
import type { RootStore } from "./root.store";
import type {
  TCustomFieldCategory,
  TCustomFieldCreateInput,
  TCustomFieldKind,
  TCustomFieldsListQuery,
  TCustomFieldSortKey,
  TCustomFieldUpdateInput,
  TCustomFieldUsage,
  TWorkspaceCustomField,
  TWorkspaceCustomFieldsPage,
} from "@/types/custom-field";

export type { TCustomFieldSortKey } from "@/types/custom-field";

export type TSortDir = "asc" | "desc";

const CUSTOM_FIELDS_DEFAULT_PER_PAGE = 50;
const FULL_LIST_PER_PAGE = 200;

interface PageState {
  results: TWorkspaceCustomField[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  totalCount: number;
  totalPages: number;
}

const EMPTY_PAGE_STATE: PageState = {
  results: [],
  nextCursor: null,
  prevCursor: null,
  hasNext: false,
  hasPrev: false,
  totalCount: 0,
  totalPages: 0,
};

function envelopeToPageState(page: TWorkspaceCustomFieldsPage): PageState {
  return {
    results: page.results,
    nextCursor: page.next_page_results ? page.next_cursor : null,
    prevCursor: page.prev_page_results ? page.prev_cursor : null,
    hasNext: page.next_page_results,
    hasPrev: page.prev_page_results,
    totalCount: page.total_count,
    totalPages: page.total_pages,
  };
}

function sortFields(fields: TWorkspaceCustomField[] | undefined | null): TWorkspaceCustomField[] {
  if (!fields) return [];
  return [...fields].sort((a, b) => a.name.localeCompare(b.name));
}

export class WorkspaceCustomFieldsStore {
  // Full-list cache used by consumers that need every live bank field
  // (intake palette, template form, intake field properties). Populated by
  // ``ensure`` / ``fetch`` — those walk all pages and concatenate.
  fieldsBySlug = observable.map<string, TWorkspaceCustomField[]>();
  // Current paginated page used by the management list. Independent cache
  // because the management list is driven by server-side filters/sort.
  pageBySlug = observable.map<string, PageState>();
  // Cursor the current page was loaded with — null means "first page".
  cursorBySlug = observable.map<string, string | null>();

  searchQuery = "";
  showArchived = false;
  kindFilter = observable.set<TCustomFieldKind>();
  /** Global Fields "Category" buckets (NG-854). Empty means every category.
   *  `system` is carried here even though the server matches no bank field on
   *  it — the list route reads this set to decide whether to inject its
   *  system rows, so dropping it would lose that signal. */
  categoryFilter = observable.set<TCustomFieldCategory>();
  sortKey: TCustomFieldSortKey = "name";
  sortDir: TSortDir = "asc";
  // Per-field usage cache. Hydrated by loadUsage (single field, Usage tab).
  usageById = observable.map<string, TCustomFieldUsage>();
  isLoading = false;
  isLoadingMore = false;
  isMutating = false;
  activeFieldId: string | null = null;
  // Last list-load failure, surfaced to the DataTable `error` prop. Cleared
  // when a (re)load starts; set when loadPage/loadMore reject.
  loadError: Error | null = null;
  // Row-selection state for the management list (drives CSV-of-selection).
  rowSelection: RowSelectionState = {};

  private rootStore: RootStore;
  // In-flight dedup. Not observable.
  private inflightFullList = new Map<string, Promise<TWorkspaceCustomField[]>>();
  private inflightPage = new Map<string, Promise<PageState>>();
  // The newest page request per workspace; only it may write the page.
  private pageRequestSeq = 0;
  private latestPageRequest = new Map<string, number>();
  private inflightUsage = new Map<string, Promise<TCustomFieldUsage>>();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(
      this,
      {
        rootStore: false,
        inflightFullList: false,
        inflightPage: false,
        pageRequestSeq: false,
        latestPageRequest: false,
        inflightUsage: false,
      } as never,
      { autoBind: true },
    );
  }

  private currentSlug(): string | null {
    return this.rootStore.router.workspaceSlug ?? null;
  }

  /** Full live bank — used by intake palette, template form, etc. */
  get fields(): TWorkspaceCustomField[] {
    const slug = this.currentSlug();
    if (!slug) return [];
    return this.fieldsBySlug.get(slug) ?? [];
  }

  /** Current paginated page for the management list. */
  get pagedFields(): TWorkspaceCustomField[] {
    const slug = this.currentSlug();
    if (!slug) return [];
    return (this.pageBySlug.get(slug) ?? EMPTY_PAGE_STATE).results;
  }

  get pagination(): PageState {
    const slug = this.currentSlug();
    if (!slug) return EMPTY_PAGE_STATE;
    return this.pageBySlug.get(slug) ?? EMPTY_PAGE_STATE;
  }

  get hasActiveFilters(): boolean {
    return (
      this.searchQuery.trim().length > 0 || this.kindFilter.size > 0 || this.categoryFilter.size > 0
    );
  }

  // ---- Filter / sort setters --------------------------------------------

  setSearchQuery(query: string): void {
    this.searchQuery = query;
  }

  setShowArchived(value: boolean): void {
    this.showArchived = value;
  }

  setKindFilter(kinds: Iterable<TCustomFieldKind>): void {
    this.kindFilter.replace([...kinds]);
  }

  setCategoryFilter(categories: Iterable<TCustomFieldCategory>): void {
    this.categoryFilter.replace([...categories]);
  }

  /** Whether the given category is currently in scope. An empty filter means
   *  "every category", so it answers true for all of them — which is what lets
   *  the list route ask about its injected system rows without special-casing
   *  the unfiltered state. */
  isCategoryInScope(category: TCustomFieldCategory): boolean {
    return this.categoryFilter.size === 0 || this.categoryFilter.has(category);
  }

  /** Single-column sort as TanStack `SortingState`, derived from the store's
   *  `sortKey`/`sortDir`. The list is always sorted (defaults to name ascending),
   *  so this never returns an empty array. */
  get sorting(): SortingState {
    return [{ id: this.sortKey, desc: this.sortDir === "desc" }];
  }

  /** Bridge a TanStack `SortingState` back into `sortKey`/`sortDir`. Column ids
   *  are the server sort tokens (name / display_id / kind / updated). Clearing
   *  the sort (3rd header click) falls back to the default name-ascending so the
   *  server query always carries a valid sort. */
  setSortingFromState(next: SortingState): void {
    const sort = next[0];
    if (sort) {
      this.sortKey = sort.id as TCustomFieldSortKey;
      this.sortDir = sort.desc ? "desc" : "asc";
    } else {
      this.sortKey = "name";
      this.sortDir = "asc";
    }
  }

  clearFilters(): void {
    this.searchQuery = "";
    this.kindFilter.clear();
    this.categoryFilter.clear();
  }

  // ---- Selection + CSV export -------------------------------------------

  setRowSelection(next: RowSelectionState): void {
    this.rowSelection = next;
  }

  /** Export the custom-fields list as CSV, scoped to the current filters/sort
   *  and (when `ids` is non-empty) restricted to the selected rows. Returns the
   *  raw Response so the caller can stream the file (200) or poll the token (202). */
  exportCustomFieldsCsv(slug: string, ids?: string[], columns?: string[]): Promise<Response> {
    const body: { ids?: string[]; columns?: string[] } = {};
    if (ids && ids.length > 0) body.ids = ids;
    if (columns && columns.length > 0) body.columns = columns;
    return this.rootStore.workspaceCustomFieldService.exportCustomFields(
      slug,
      body,
      this.buildListQuery(null),
    );
  }

  /** Poll an async export by token until its presigned URL is ready (or null). */
  pollExport(slug: string, token: string): Promise<string | null> {
    return pollExportStatus(token, (tok) =>
      this.rootStore.workspaceCustomFieldService.getExportStatus(slug, tok),
    );
  }

  // ---- Full-list fetch (walks every page) -------------------------------

  /**
   * Materialize the entire live bank (archived + active) by walking pages.
   * Used by callers that legitimately need every field (palette, template
   * editor). The management list does NOT call this — it pages explicitly.
   */
  async fetch(slug: string): Promise<TWorkspaceCustomField[]> {
    this.isLoading = true;
    try {
      const all: TWorkspaceCustomField[] = [];
      let cursor: string | null = null;
      while (true) {
        const page: TWorkspaceCustomFieldsPage =
          await this.rootStore.workspaceCustomFieldService.list(slug, {
            per_page: FULL_LIST_PER_PAGE,
            include_archived: true,
            cursor: cursor ?? undefined,
          });
        all.push(...page.results);
        if (!page.next_page_results) break;
        cursor = page.next_cursor;
      }
      runInAction(() => {
        this.fieldsBySlug.set(slug, sortFields(all));
      });
      return all;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  /**
   * Load the bank for a workspace if not already cached. Concurrent calls for
   * the same slug share a single in-flight request.
   */
  async ensure(slug: string): Promise<TWorkspaceCustomField[]> {
    const cached = this.fieldsBySlug.get(slug);
    if (cached) return cached;
    const inflight = this.inflightFullList.get(slug);
    if (inflight) return inflight;
    const p = this.fetch(slug).finally(() => {
      this.inflightFullList.delete(slug);
    });
    this.inflightFullList.set(slug, p);
    return p;
  }

  // ---- Paginated page fetch (management list) ---------------------------

  private buildListQuery(cursor: string | null): TCustomFieldsListQuery {
    const q: TCustomFieldsListQuery = {
      per_page: CUSTOM_FIELDS_DEFAULT_PER_PAGE,
      sort: this.sortKey,
      dir: this.sortDir,
      include_archived: this.showArchived,
    };
    if (this.searchQuery.trim()) q.q = this.searchQuery.trim();
    if (this.kindFilter.size > 0) q.kind = [...this.kindFilter];
    if (this.categoryFilter.size > 0) q.category = [...this.categoryFilter];
    if (cursor) q.cursor = cursor;
    return q;
  }

  /**
   * Fetch one page of fields with the current filter / sort state.
   * ``cursor=null`` loads the first page; pass ``pagination.nextCursor`` or
   * ``prevCursor`` to walk.
   *
   * A read joins an identical request already in flight, unless ``fresh``: a
   * refresh after a write must not join a read sent before the write, whose
   * page predates it (NG-2562). Only the newest request for a workspace writes
   * the page, so an older response arriving late cannot overwrite it.
   */
  async loadPage(
    slug: string,
    cursor: string | null = null,
    { fresh = false }: { fresh?: boolean } = {},
  ): Promise<void> {
    const query = this.buildListQuery(cursor);
    const key = `${slug}:${JSON.stringify(query)}`;
    const existing = this.inflightPage.get(key);
    if (existing && !fresh) {
      await existing;
      return;
    }
    const seq = ++this.pageRequestSeq;
    this.latestPageRequest.set(slug, seq);
    const isLatest = () => this.latestPageRequest.get(slug) === seq;
    const promise: Promise<PageState> = (async () => {
      runInAction(() => {
        this.isLoading = true;
        this.loadError = null;
      });
      try {
        const page = await this.rootStore.workspaceCustomFieldService.list(slug, query);
        const state = envelopeToPageState(page);
        if (isLatest()) {
          runInAction(() => {
            this.pageBySlug.set(slug, state);
            this.cursorBySlug.set(slug, cursor);
          });
        }
        return state;
      } catch (err) {
        if (isLatest()) {
          runInAction(() => {
            this.loadError = err instanceof Error ? err : new Error(String(err));
          });
        }
        throw err;
      } finally {
        if (isLatest()) {
          runInAction(() => {
            this.isLoading = false;
          });
        }
      }
    })().finally(() => {
      if (this.inflightPage.get(key) === promise) this.inflightPage.delete(key);
    });
    this.inflightPage.set(key, promise);
    await promise;
  }

  /** Reload the current page with current filters, after a write: always a
   *  new request, never one already in flight (NG-2562). */
  async refreshCurrentPage(slug: string): Promise<void> {
    const cursor = this.cursorBySlug.get(slug) ?? null;
    await this.loadPage(slug, cursor, { fresh: true });
  }

  /** Append the next page to the current results (DataTable infinite scroll).
   *  No-op when there's no next page or a fetch is already in flight; merged
   *  results are deduped by id. */
  async loadMore(slug: string): Promise<void> {
    const current = this.pageBySlug.get(slug);
    // No page yet (initial load failed) — the DataTable error-empty "Try again"
    // routes the retry here, so re-run the first page rather than no-opping.
    if (!current) {
      await this.loadPage(slug, null);
      return;
    }
    if (!current.hasNext || !current.nextCursor) return;
    if (this.isLoadingMore) return;
    const query = this.buildListQuery(current.nextCursor);
    runInAction(() => {
      this.isLoadingMore = true;
      this.loadError = null;
    });
    try {
      const page = await this.rootStore.workspaceCustomFieldService.list(slug, query);
      const next = envelopeToPageState(page);
      runInAction(() => {
        const existing = this.pageBySlug.get(slug);
        const seen = new Set((existing?.results ?? []).map((f) => f.id));
        const merged = [
          ...(existing?.results ?? []),
          ...next.results.filter((f) => !seen.has(f.id)),
        ];
        this.pageBySlug.set(slug, { ...next, results: merged });
        this.cursorBySlug.set(slug, current.nextCursor);
      });
    } catch (err) {
      runInAction(() => {
        this.loadError = err instanceof Error ? err : new Error(String(err));
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoadingMore = false;
      });
    }
  }

  // ---- Mutations --------------------------------------------------------
  // Mutations patch the full-list cache locally (cheap, immediate) and
  // refresh the current management page from the server (so server-side
  // sort/filter membership stays accurate).

  async create(
    slug: string,
    input: TCustomFieldCreateInput,
    source: AnalyticsSource = "page",
  ): Promise<TWorkspaceCustomField> {
    this.isMutating = true;
    try {
      const created = await this.rootStore.workspaceCustomFieldService.create(slug, input);
      runInAction(() => {
        const current = this.fieldsBySlug.get(slug);
        if (current) this.fieldsBySlug.set(slug, sortFields([...current, created]));
      });
      // Before the page refresh, so a failed reload can't drop it.
      track("custom_field_created", { field_id: created.id, kind: created.kind, source });
      if (this.pageBySlug.has(slug)) {
        await this.refreshCurrentPage(slug);
      }
      return created;
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async update(
    slug: string,
    fieldId: string,
    patch: TCustomFieldUpdateInput,
    source: AnalyticsSource = "page",
  ): Promise<TWorkspaceCustomField> {
    this.activeFieldId = fieldId;
    // Read before the write: `fields` is what this save changed (NG-2064).
    const fields = changedFields(patch, this.cachedField(slug, fieldId));
    try {
      const updated = await this.rootStore.workspaceCustomFieldService.update(slug, fieldId, patch);
      runInAction(() => {
        const full = this.fieldsBySlug.get(slug);
        if (full) {
          this.fieldsBySlug.set(
            slug,
            sortFields(full.map((f) => (f.id === fieldId ? updated : f))),
          );
        }
        const page = this.pageBySlug.get(slug);
        if (page) {
          this.pageBySlug.set(slug, {
            ...page,
            results: page.results.map((f) => (f.id === fieldId ? updated : f)),
          });
        }
      });
      track("custom_field_updated", { field_id: fieldId, kind: updated.kind, fields, source });
      return updated;
    } finally {
      runInAction(() => {
        this.activeFieldId = null;
      });
    }
  }

  async archive(slug: string, fieldId: string, source: AnalyticsSource = "page"): Promise<void> {
    this.activeFieldId = fieldId;
    try {
      await this.rootStore.workspaceCustomFieldService.archive(slug, fieldId);
      track("custom_field_archived", { field_id: fieldId, source });
      // Backend returns 204; refetch the full cache so existing consumers
      // (palette, etc.) see the new archived_at. Also refresh the current
      // paginated page if one is loaded.
      if (this.fieldsBySlug.has(slug)) await this.fetch(slug);
      if (this.pageBySlug.has(slug)) await this.refreshCurrentPage(slug);
    } finally {
      runInAction(() => {
        this.activeFieldId = null;
      });
    }
  }

  async restore(
    slug: string,
    fieldId: string,
    source: AnalyticsSource = "page",
  ): Promise<TWorkspaceCustomField> {
    this.activeFieldId = fieldId;
    try {
      const restored = await this.rootStore.workspaceCustomFieldService.restore(slug, fieldId);
      track("custom_field_restored", { field_id: fieldId, source });
      runInAction(() => {
        const full = this.fieldsBySlug.get(slug);
        if (full) {
          this.fieldsBySlug.set(
            slug,
            full.map((f) => (f.id === fieldId ? restored : f)),
          );
        }
      });
      if (this.pageBySlug.has(slug)) await this.refreshCurrentPage(slug);
      return restored;
    } finally {
      runInAction(() => {
        this.activeFieldId = null;
      });
    }
  }

  async hardDelete(slug: string, fieldId: string, source: AnalyticsSource = "page"): Promise<void> {
    this.activeFieldId = fieldId;
    try {
      await this.rootStore.workspaceCustomFieldService.hardDelete(slug, fieldId);
      track("custom_field_deleted", { field_id: fieldId, source });
      runInAction(() => {
        const full = this.fieldsBySlug.get(slug);
        if (full) {
          this.fieldsBySlug.set(
            slug,
            full.filter((f) => f.id !== fieldId),
          );
        }
      });
      if (this.pageBySlug.has(slug)) await this.refreshCurrentPage(slug);
    } finally {
      runInAction(() => {
        this.activeFieldId = null;
      });
    }
  }

  async duplicate(
    slug: string,
    fieldId: string,
    source: AnalyticsSource = "page",
  ): Promise<TWorkspaceCustomField> {
    this.activeFieldId = fieldId;
    try {
      const clone = await this.rootStore.workspaceCustomFieldService.duplicate(slug, fieldId);
      runInAction(() => {
        const full = this.fieldsBySlug.get(slug);
        if (full) this.fieldsBySlug.set(slug, sortFields([...full, clone]));
      });
      track("custom_field_duplicated", {
        field_id: clone.id,
        duplicated_from_id: fieldId,
        source,
      });
      if (this.pageBySlug.has(slug)) await this.refreshCurrentPage(slug);
      return clone;
    } finally {
      runInAction(() => {
        this.activeFieldId = null;
      });
    }
  }

  /** The field as the full cache or the loaded management page holds it. */
  private cachedField(slug: string, fieldId: string): TWorkspaceCustomField | undefined {
    return (
      this.fieldsBySlug.get(slug)?.find((f) => f.id === fieldId) ??
      this.pageBySlug.get(slug)?.results.find((f) => f.id === fieldId)
    );
  }

  // ---- Usage cache ------------------------------------------------------

  async loadUsage(slug: string, fieldId: string): Promise<TCustomFieldUsage> {
    const cached = this.usageById.get(fieldId);
    if (cached) return cached;
    const key = `${slug}:${fieldId}`;
    const inflight = this.inflightUsage.get(key);
    if (inflight) return inflight;
    const p = this.rootStore.workspaceCustomFieldService
      .usage(slug, fieldId)
      .then((usage) => {
        runInAction(() => {
          this.usageById.set(fieldId, usage);
        });
        return usage;
      })
      .finally(() => {
        this.inflightUsage.delete(key);
      });
    this.inflightUsage.set(key, p);
    return p;
  }
}
